import { lazy } from 'react';
import type { createBrowserRouter } from 'react-router-dom';
import { Navigate } from 'react-router-dom';
import { ImportSharedViewDialog as CalendarChartImportSharedViewDialog } from '@/pages/CalendarChart';
import { ImportSharedViewDialog as CalendarInsightsImportSharedViewDialog } from '@/pages/CalendarInsights';
import { ImportSharedViewDialog as CohortChartImportSharedViewDialog } from '@/pages/CohortChart';
import { ImportSharedViewDialog as CohortMarketingPerformanceImportSharedViewDialog } from '@/pages/CohortMarketingPerformance';
import { ViewType } from '@/entities/session';
import { ROUTES, SHARE_ROUTE } from '@/shared/constants';
import { Features } from '@/shared/ui';
import { AuthGuard } from '../ui/AuthGuard/AuthGuard';
import { Baseline } from '../ui/Baseline/Baseline';
import { DataStatusGuard } from '../ui/DataStatusGuard/DataStatusGuard';
import { FeatureGuard } from '../ui/FeatureGuard/FeatureGuard';
import { GlobalErrorBoundary } from '../ui/GlobalErrorBoundary/GlobalErrorBoundary';
import { GuestGuard } from '../ui/GuestGuard/GuestGuard';
import { ViewsRestrictionsGuard } from '../ui/ViewsRestrictionsGuard/ViewsRestrictionsGuard';
// use of lazy from react-router causes an error:
// useViewInitializationStateMachineContext hook used outside <ViewInitializationStateMachineProvider />
const CohortMarketingPerformancePage = lazy(() =>
  import('@/pages/CohortMarketingPerformance').then(
    ({ CohortMarketingPerformancePage }) => ({
      default: CohortMarketingPerformancePage,
    }),
  ),
);
const CalendarInsightsPage = lazy(() =>
  import('@/pages/CalendarInsights').then(({ CalendarInsightsPage }) => ({
    default: CalendarInsightsPage,
  })),
);
const CohortChartPage = lazy(() =>
  import('@/pages/CohortChart').then(({ CohortChartPage }) => ({
    default: CohortChartPage,
  })),
);
const CalendarChartPage = lazy(() =>
  import('@/pages/CalendarChart').then(({ CalendarChartPage }) => ({
    default: CalendarChartPage,
  })),
);
const DatasourcesPage = lazy(() =>
  import('@/pages/Datasources').then(({ DatasourcesPage }) => ({
    default: DatasourcesPage,
  })),
);
const DatasourcePage = lazy(() =>
  import('@/pages/Datasource').then(({ DatasourcePage }) => ({
    default: DatasourcePage,
  })),
);
const PasswordRestorePage = lazy(() =>
  import('@/pages/PasswordRestore').then(({ PasswordRestorePage }) => ({
    default: PasswordRestorePage,
  })),
);
const SignInPage = lazy(() =>
  import('@/pages/SignIn').then(({ SignInPage }) => ({
    default: SignInPage,
  })),
);
const PasswordResetPage = lazy(() =>
  import('@/pages/PasswordReset').then(({ PasswordResetPage }) => ({
    default: PasswordResetPage,
  })),
);
const CohortRetentionPage = lazy(() =>
  import('@/pages/CohortRetention').then(({ CohortRetentionPage }) => ({
    default: CohortRetentionPage,
  })),
);
const CohortRetentionImportSharedViewDialog = lazy(() =>
  import('@/pages/CohortRetention').then(({ ImportSharedViewDialog }) => ({
    default: ImportSharedViewDialog,
  })),
);
const ROIBreakdownPage = lazy(() =>
  import('@/pages/ROIBreakdown').then(({ ROIBreakdownPage }) => ({
    default: ROIBreakdownPage,
  })),
);
const ROIBreakdownImportSharedViewDialog = lazy(() =>
  import('@/pages/ROIBreakdown').then(({ ImportSharedViewDialog }) => ({
    default: ImportSharedViewDialog,
  })),
);
const CohortScannerPage = lazy(() =>
  import('@/pages/CohortScanner').then(({ CohortScannerPage }) => ({
    default: CohortScannerPage,
  })),
);
const CohortScannerImportSharedViewDialog = lazy(() =>
  import('@/pages/CohortScanner').then(({ ImportSharedViewDialog }) => ({
    default: ImportSharedViewDialog,
  })),
);
const UserManagementPage = lazy(() =>
  import('@/pages/UserManagement').then(({ UserManagementPage }) => ({
    default: UserManagementPage,
  })),
);
const ProductsCatalogPage = lazy(() =>
  import('@/pages/ProductsCatalog').then(({ ProductsCatalogPage }) => ({
    default: ProductsCatalogPage,
  })),
);
const ProductPage = lazy(() =>
  import('@/pages/ProductPage').then(({ ProductPage }) => ({
    default: ProductPage,
  })),
);
const OrganizationInfoPage = lazy(() =>
  import('@/pages/OrganizationInfo').then(({ OrganizationInfoPage }) => ({
    default: OrganizationInfoPage,
  })),
);
const UserSettingsPage = lazy(() =>
  import('@/pages/UserSettings').then(({ UserSettingsPage }) => ({
    default: UserSettingsPage,
  })),
);
const DashboardsPage = lazy(() =>
  import('@/pages/Dashboards').then(({ DashboardsPage }) => ({
    default: DashboardsPage,
  })),
);
const DashboardPage = lazy(() =>
  import('@/pages/Dashboard').then(({ DashboardPage }) => ({
    default: DashboardPage,
  })),
);
const DashboardPageWrapper = lazy(() =>
  import('@/widgets/dashboards').then(({ PageWrapper }) => ({
    default: PageWrapper,
  })),
);
const OverviewPage = lazy(() =>
  import('@/pages/Overview').then(({ OverviewPage }) => {
    return {
      default: OverviewPage,
    };
  }),
);
const PresetsPage = lazy(() =>
  import('@/pages/Presets').then(({ PresetsPage }) => ({
    default: PresetsPage,
  })),
);

export const appRouter = (
  createBrowserRouterFunction: typeof createBrowserRouter,
) => {
  return createBrowserRouterFunction([
    {
      path: ROUTES.home,
      element: <Baseline />,
      errorElement: <GlobalErrorBoundary />,
      children: [
        {
          element: <AuthGuard />,
          children: [
            {
              index: true,
              lazy: () =>
                import('@/pages/Home').then(({ HomePage }) => ({
                  Component: HomePage,
                })),
            },
            {
              path: ROUTES.cohortMarketingPerformance,
              element: (
                <DataStatusGuard>
                  <ViewsRestrictionsGuard
                    view={ViewType.cohortMarketingPerformance}
                  >
                    <CohortMarketingPerformancePage />
                  </ViewsRestrictionsGuard>
                </DataStatusGuard>
              ),
              children: [
                {
                  path: SHARE_ROUTE,
                  element: <CohortMarketingPerformanceImportSharedViewDialog />,
                },
              ],
            },
            {
              path: ROUTES.calendarInsights,
              element: (
                <DataStatusGuard>
                  <ViewsRestrictionsGuard view={ViewType.calendarInsights}>
                    <CalendarInsightsPage />
                  </ViewsRestrictionsGuard>
                </DataStatusGuard>
              ),
              children: [
                {
                  path: SHARE_ROUTE,
                  element: <CalendarInsightsImportSharedViewDialog />,
                },
              ],
            },
            {
              path: ROUTES.cohortChart,
              element: (
                <DataStatusGuard>
                  <ViewsRestrictionsGuard view={ViewType.cohortChart}>
                    <CohortChartPage />
                  </ViewsRestrictionsGuard>
                </DataStatusGuard>
              ),
              children: [
                {
                  path: SHARE_ROUTE,
                  element: <CohortChartImportSharedViewDialog />,
                },
              ],
            },
            {
              path: ROUTES.calendarChart,
              element: (
                <ViewsRestrictionsGuard view={ViewType.calendarChart}>
                  <CalendarChartPage />
                </ViewsRestrictionsGuard>
              ),
              children: [
                {
                  path: SHARE_ROUTE,
                  element: <CalendarChartImportSharedViewDialog />,
                },
              ],
            },
            {
              path: ROUTES.cohortRetention,
              element: (
                <DataStatusGuard>
                  <ViewsRestrictionsGuard view={ViewType.cohortRetention}>
                    <CohortRetentionPage />
                  </ViewsRestrictionsGuard>
                </DataStatusGuard>
              ),
              children: [
                {
                  path: SHARE_ROUTE,
                  element: <CohortRetentionImportSharedViewDialog />,
                },
              ],
            },
            {
              path: ROUTES.roiBreakdown,
              element: (
                <DataStatusGuard>
                  <ViewsRestrictionsGuard view={ViewType.roiBreakdown}>
                    <ROIBreakdownPage />
                  </ViewsRestrictionsGuard>
                </DataStatusGuard>
              ),
              children: [
                {
                  path: SHARE_ROUTE,
                  element: <ROIBreakdownImportSharedViewDialog />,
                },
              ],
            },
            {
              path: ROUTES.cohortScanner,
              element: (
                <DataStatusGuard>
                  <ViewsRestrictionsGuard view={ViewType.cohortScanner}>
                    <CohortScannerPage />
                  </ViewsRestrictionsGuard>
                </DataStatusGuard>
              ),
              children: [
                {
                  path: SHARE_ROUTE,
                  element: <CohortScannerImportSharedViewDialog />,
                },
              ],
            },
            {
              path: ROUTES.dashboards,
              element: (
                <FeatureGuard feature={Features.dashboards}>
                  <DashboardPageWrapper />
                </FeatureGuard>
              ),
              children: [
                {
                  index: true,
                  element: <DashboardsPage />,
                },
                {
                  path: ':dashboardId',
                  element: <DashboardPage />,
                },
              ],
            },
            {
              path: ROUTES.passwordRestore,
              element: <PasswordRestorePage />,
            },
            {
              path: ROUTES.datasources,
              children: [
                {
                  index: true,
                  element: (
                    <ViewsRestrictionsGuard view={ViewType.integrations}>
                      <DatasourcesPage />
                    </ViewsRestrictionsGuard>
                  ),
                },
                {
                  path: ':datasourceId',
                  element: (
                    <ViewsRestrictionsGuard view={ViewType.integrations}>
                      <DatasourcePage />
                    </ViewsRestrictionsGuard>
                  ),
                },
              ],
            },
            {
              path: ROUTES.productsCatalog,
              children: [
                {
                  index: true,
                  element: <ProductsCatalogPage />,
                },
                {
                  path: ':productId',
                  element: <ProductPage />,
                },
              ],
            },
            {
              path: ROUTES.overview,
              element: <FeatureGuard feature={Features.overview} />,
              children: [
                {
                  index: true,
                  element: <OverviewPage />,
                },
              ],
            },
            {
              path: ROUTES.presetsPage,
              element: <FeatureGuard feature={Features.presetsPage} />,
              children: [
                {
                  index: true,
                  element: <PresetsPage />,
                },
              ],
            },
            {
              path: ROUTES.userManagement,
              element: <UserManagementPage />,
            },
            {
              path: ROUTES.organizationInfo,
              element: <OrganizationInfoPage />,
            },
            {
              path: ROUTES.userSettings,
              element: <UserSettingsPage />,
            },
            { path: '*', element: <Navigate to={ROUTES.home} /> },
          ],
        },
        {
          element: <GuestGuard />,
          children: [
            { path: ROUTES.signin, element: <SignInPage /> },
            { path: ROUTES.passwordReset, element: <PasswordResetPage /> },
          ],
        },
      ],
    },
  ]);
};
